import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { ReactElement } from 'react';
import { S_TimePeriodSelector, TimePeriod } from '../../../../../../../design/selects/timePeriodSelector/S_TimePeriodSelector';
import { ICurrentWorkspaceStore } from '../../../../../../workspaces/currentWorkspace/CurrentWorkspace.store';
import { IProjectStatsFiltersBl } from '../../ProjectStatsFilters.bl';
import { IProjectStatsFiltersStore } from '../../ProjectStatsFilters.store';
import { DateRangePicker } from './datePickers/DateRangePicker';
import { DayPicker } from './datePickers/DayPicker';
import { MonthPicker } from './datePickers/monthPicker/MonthPicker';
import { WeekPicker } from './datePickers/weekPicker/WeekPicker';

export const TimePeriodSelectorContainer = observer((): ReactElement => {
  const bl = useInjection(IProjectStatsFiltersBl.$);
  const store = useInjection(IProjectStatsFiltersStore.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);

  const handleClickNext = async (): Promise<void> => {
    await bl.goToNextDateAsync();
  };

  const handleClickPrevious = async (): Promise<void> => {
    await bl.goToPreviousDateAsync();
  };

  const handleTimePeriodChange = async (timePeriod: TimePeriod): Promise<void> => {
    await bl.selectTimePeriodAsync(timePeriod);
  };

  const handleSetDate = async (date: Date): Promise<void> => {
    await bl.selectStartDateAsync(date);
  };

  const handleSetDates = async (startDate: Date, endDate: Date): Promise<void> => {
    await bl.selectDatesAsync(startDate, endDate);
  };

  const getMinDate = (): Date => {
    const projectCreatedDate = store.projectCreatedDate.clone();
    let minDate = new Date();

    switch (store.timePeriod) {
      case TimePeriod.Day:
      case TimePeriod.DateRange:
        minDate = new Date(projectCreatedDate.startOf('day').utcOffset(0, true).format());
        break;
      case TimePeriod.Week:
        minDate = new Date(projectCreatedDate.startOf('week').add(1, 'day').utcOffset(0, true).format());
        break;
      case TimePeriod.Month:
        minDate = new Date(projectCreatedDate.startOf('month').utcOffset(0, true).format());
        break;
      case TimePeriod.All:
        break;
    }

    minDate.setMinutes(minDate.getMinutes() + minDate.getTimezoneOffset());
    return minDate;
  };

  const getMaxDate = (): Date => {
    const maxDateMoment = moment(currentWorkspaceStore.currentWorkspaceTime).utcOffset(0, true);

    if (store.timePeriod === TimePeriod.Week) {
      maxDateMoment.endOf('week').add(1, 'day');
    }
    const maxDate = new Date(maxDateMoment.format());
    maxDate.setMinutes(maxDate.getMinutes() + maxDate.getTimezoneOffset());
    return maxDate;
  };

  const renderTimePicker = (): ReactElement => {
    const selectedStartDate = new Date(store.startDate.format());
    selectedStartDate.setMinutes(selectedStartDate.getMinutes() + selectedStartDate.getTimezoneOffset());

    const selectedEndDate = new Date(store.endDate.format());
    selectedEndDate.setMinutes(selectedEndDate.getMinutes() + selectedEndDate.getTimezoneOffset());

    const minDate = getMinDate();
    const maxDate = getMaxDate();

    switch (store.timePeriod) {
      case TimePeriod.Day:
        return (
          <DayPicker
            selectedDate={selectedStartDate}
            onSetDate={handleSetDate}
            minDate={minDate}
            maxDate={maxDate}
            onClickNext={handleClickNext}
            onClickPrevious={handleClickPrevious}
            canClickNext={bl.canGoToNextDate()}
            canClickPrevious={bl.canGoToPreviousDate()}
          />
        );

      case TimePeriod.Month:
        return (
          <MonthPicker
            selectedDate={selectedStartDate}
            onSetDate={handleSetDate}
            minDate={minDate}
            maxDate={maxDate}
            onClickNext={handleClickNext}
            onClickPrevious={handleClickPrevious}
            canClickNext={bl.canGoToNextDate()}
            canClickPrevious={bl.canGoToPreviousDate()}
          />
        );

      case TimePeriod.DateRange:
        return (
          <DateRangePicker
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            onSetDates={handleSetDates}
            minDate={minDate}
            maxDate={maxDate}
          />
        );

      case TimePeriod.Week:
        return (
          <WeekPicker
            selectedDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            onSetDate={handleSetDate}
            minDate={minDate}
            maxDate={maxDate}
            onClickNext={handleClickNext}
            onClickPrevious={handleClickPrevious}
            canClickNext={bl.canGoToNextDate()}
            canClickPrevious={bl.canGoToPreviousDate()}
          />
        );

      case TimePeriod.All:
      default:
        return <div className='date-range-field' />;
    }
  };

  return (
    <>
      <S_TimePeriodSelector
        selectedTimePeriod={store.timePeriod}
        onPeriodChange={handleTimePeriodChange}
        includeCustomRangeOption
      />
      {renderTimePicker()}
    </>
  );
});
